import { Card, Giveaway, GiveawayRequirements, Listing, Pack, PackOpenResult, Transaction, User, UserCard } from './types';

const BASE_URL = '';

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Get Telegram initData from window.Telegram.WebApp */
function getInitData(): string {
  return window.Telegram?.WebApp?.initData ?? '';
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  requireAuth = true,
): Promise<T> {
  const initData = getInitData();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (requireAuth && initData) {
    headers['X-Telegram-Init-Data'] = initData;
  }
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: { ...headers, ...options.headers as Record<string, string> },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText })) as { error?: string };
    throw new ApiError(res.status, body.error ?? res.statusText);
  }

  return res.json() as Promise<T>;
}

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const auth = {
  login: () => request<{ user: User }>('/api/auth', { method: 'POST' }),
};

// ─── User ─────────────────────────────────────────────────────────────────────
export const user = {
  me: () => request<User>('/api/user/me'),
  transactions: (limit = 20, offset = 0) =>
    request<{ transactions: Transaction[]; limit: number; offset: number }>(
      `/api/user/me/transactions?limit=${limit}&offset=${offset}`,
    ),
};

// ─── Packs ────────────────────────────────────────────────────────────────────
export const packs = {
  list: () => request<{ packs: Pack[] }>('/api/packs', {}, false),
  buy: (packId: number, txHash: string) =>
    request<PackOpenResult>(`/api/packs/${packId}/buy`, {
      method: 'POST',
      body: JSON.stringify({ tx_hash: txHash }),
    }),
  open: async (packId: number, tgId: string, tgName: string): Promise<PackOpenResult> => {
    const res = await fetch(`${BASE_URL}/api/packs/${packId}/open`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tg_id: tgId, tg_name: tgName }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: res.statusText })) as { error?: string };
      throw new Error(body.error ?? res.statusText);
    }
    return res.json() as Promise<PackOpenResult>;
  },
};

// ─── Cards ────────────────────────────────────────────────────────────────────
export const cards = {
  catalog: (params?: { rarity?: string; set_code?: string; search?: string; limit?: number; offset?: number }) => {
    const q = new URLSearchParams();
    if (params?.rarity) q.set('rarity', params.rarity);
    if (params?.set_code) q.set('set_code', params.set_code);
    if (params?.search) q.set('search', params.search);
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    return request<{ cards: Card[] }>(`/api/cards/catalog?${q}`, {}, false);
  },
  my: (sort?: string, limit = 50, offset = 0) =>
    request<{ cards: UserCard[] }>(
      `/api/cards/my?sort=${sort ?? 'newest'}&limit=${limit}&offset=${offset}`,
    ),
  detail: (id: number) => request<{ card: Card }>(`/api/cards/${id}`),
};

// ─── Marketplace ──────────────────────────────────────────────────────────────
export const marketplace = {
  list: (params?: { rarity?: string; sort?: string; search?: string; limit?: number; offset?: number }) => {
    const q = new URLSearchParams();
    if (params?.rarity) q.set('rarity', params.rarity);
    if (params?.sort) q.set('sort', params.sort);
    if (params?.search) q.set('search', params.search);
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    return request<{ listings: Listing[] }>(`/api/marketplace?${q}`, {}, false);
  },
  listCard: (userCardId: number, priceTon: number) =>
    request<{ listing_id: number }>('/api/marketplace/list', {
      method: 'POST',
      body: JSON.stringify({ user_card_id: userCardId, price_ton: priceTon }),
    }),
  buy: (listingId: number, tgId: string, tgName: string) =>
    request<{ success: boolean; price_ton: number; fee_ton: number }>(
      `/api/marketplace/${listingId}/buy`,
      { method: 'POST', body: JSON.stringify({ tg_id: tgId, tg_name: tgName }) },
    ),
  makeOffer: (listingId: number, priceTon: number) =>
    request<{ offer_id: number }>(`/api/marketplace/${listingId}/offer`, {
      method: 'POST',
      body: JSON.stringify({ price_ton: priceTon }),
    }),
  acceptOffer: (offerId: number) =>
    request<{ success: boolean }>(`/api/marketplace/offers/${offerId}/accept`, {
      method: 'POST',
    }),
  cancel: (listingId: number) =>
    request<{ success: boolean }>(`/api/marketplace/${listingId}`, {
      method: 'DELETE',
    }),
};

// ─── Giveaways ────────────────────────────────────────────────────────────────
export const giveaways = {
  list: () => request<{ giveaways: Giveaway[] }>('/api/giveaways', {}, false),
  get: (id: number, tgId?: string) => {
    const q = tgId ? `?tg_id=${encodeURIComponent(tgId)}` : '';
    return request<Giveaway>(`/api/giveaways/${id}${q}`, {}, false);
  },
  create: (data: {
    user_card_id: number;
    channel_username?: string;
    duration_hours: number;
    requirements?: GiveawayRequirements;
    tg_id: string;
    tg_name: string;
  }) =>
    request<{ giveaway_id: number }>('/api/giveaways', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  enter: (id: number, tgId: string, tgName: string) =>
    request<{ success: boolean; entry_count: number }>(`/api/giveaways/${id}/enter`, {
      method: 'POST',
      body: JSON.stringify({ tg_id: tgId, tg_name: tgName }),
    }),
  resolve: () => request<{ resolved: number }>('/api/giveaways/resolve', {}, false),
};

export { ApiError };

// Telegram WebApp type shim
declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData: string;
        initDataUnsafe?: {
          user?: {
            id?: number;
            username?: string;
            first_name?: string;
            last_name?: string;
            is_premium?: boolean;
          };
        };
        ready: () => void;
        expand: () => void;
        close: () => void;
        MainButton: {
          text: string;
          show: () => void;
          hide: () => void;
          onClick: (fn: () => void) => void;
        };
      };
    };
  }
}
